// src/Pages/ApiKeys/ApiKeysPage.tsx

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button, TextInput, Select, SelectItem } from "@tremor/react";
import { KeyIcon, PlusIcon, SearchIcon } from "lucide-react";
import { useApiKeys } from "@/hooks/apiKeys/useApiKeys";
import useAppStore from "@/store/AppStore";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import ApiKeyList from "./ApiKeyList";
import ApiKeyStats from "./ApiKeyStats";
import CreateApiKeyModal from "./CreateApiKeyModal";
import ApiKeySuccessModal from "./ApiKeySuccessModal";
import type { ApiKey } from "@/types/apiKeys";

export default function ApiKeysPage() {
  const navigate = useNavigate();
  const appStore = useAppStore() as { user: { role: string } };
  const axiosPrivate = useAxiosPrivate();
  
  // Check if user is admin
  useEffect(() => {
    if (appStore.user?.role === "user") {
      navigate("/");
    }
  }, [appStore.user, navigate]);

  const [selectedOrgId, setSelectedOrgId] = useState<number | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const [organizations, setOrganizations] = useState<{ id: number; name: string }[]>([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [newKeyDetails, setNewKeyDetails] = useState<{ key: string; name: string } | null>(null);

  const {
    keys,
    stats,
    recentUsage,
    loading,
    error,
    createKey,
    toggleKeyStatus,
    revokeKey,
    clearCreatedKey,
  } = useApiKeys({
    orgId: selectedOrgId,
    autoLoad: true,
  });

  // Fetch organizations for filter
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axiosPrivate.get("/auth/orgs");
        setOrganizations(response.data.data);
      } catch (error) {
        console.error("Failed to fetch organizations:", error);
      }
    };
    
    fetchOrganizations();
  }, [axiosPrivate]);

  // Filter keys based on search term
  const filteredKeys = keys.filter((key: ApiKey) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      key.name.toLowerCase().includes(term) ||
      key.org_name.toLowerCase().includes(term) ||
      key.key_prefix.toLowerCase().includes(term) ||
      (key.description && key.description.toLowerCase().includes(term))
    );
  });

  const handleCreateKey = async (data: any) => {
    try {
      const response = await createKey(data);
      setNewKeyDetails({
        key: response.data.key,
        name: response.data.name,
      });
      setCreateModalOpen(false);
      setSuccessModalOpen(true);
    } catch (error) {
      // Error is handled in the hook
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
    setNewKeyDetails(null);
    clearCreatedKey();
  };

  const handleOrgFilterChange = (value: string) => {
    const orgId = value === "all" ? undefined : Number.parseInt(value);
    setSelectedOrgId(orgId);
  };

  return (
    <div className="flex-1 space-y-4 p-8 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <KeyIcon className="h-8 w-8 text-gray-700" />
          <h2 className="text-3xl font-bold tracking-tight">
            Διαχείριση API Keys
          </h2>
        </div>
        <Button
          onClick={() => setCreateModalOpen(true)}
          icon={PlusIcon}
          color="blue"
        >
          Δημιουργία API Key
        </Button>
      </div>

      {/* Admin Only Notice */}
      {(appStore.user?.role === "admin" || appStore.user?.role === "superAdmin") && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Admin Access:</strong> Μπορείτε να διαχειριστείτε API keys για όλους τους οργανισμούς.
            Κάθε οργανισμός μπορεί να έχει μέχρι 10 ενεργά API keys.
          </p>
        </div>
      )}

      {/* Statistics */}
      {stats && (
        <ApiKeyStats
          stats={stats}
          recentUsage={recentUsage}
          loading={loading}
        />
      )}

      {/* Main Content */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">Όλα τα Keys</TabsTrigger>
          <TabsTrigger value="active">Ενεργά</TabsTrigger>
          <TabsTrigger value="inactive">Ανενεργά</TabsTrigger>
        </TabsList>

        {/* Filters */}
        <div className="flex space-x-4 items-end">
          <div className="flex-1 max-w-sm">
            <TextInput
              placeholder="Αναζήτηση με όνομα, οργανισμό ή prefix..."
              value={searchTerm}
              onValueChange={setSearchTerm}
              icon={SearchIcon}
            />
          </div>
          <div className="w-64">
            <Select
              value={selectedOrgId?.toString() || "all"}
              onValueChange={handleOrgFilterChange}
              placeholder="Φίλτρο ανά οργανισμό"
            >
              <SelectItem value="all">Όλοι οι οργανισμοί</SelectItem>
              {organizations.map(org => (
                <SelectItem key={org.id} value={org.id.toString()}>
                  {org.name}
                </SelectItem>
              ))}
            </Select>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Tabs Content */}
        <TabsContent value="all" className="space-y-4">
          <ApiKeyList
            keys={filteredKeys}
            onToggleStatus={toggleKeyStatus}
            onRevoke={revokeKey}
            loading={loading}
          />
        </TabsContent>

        <TabsContent value="active" className="space-y-4">
          <ApiKeyList
            keys={filteredKeys.filter(key => key.is_active)}
            onToggleStatus={toggleKeyStatus}
            onRevoke={revokeKey}
            loading={loading}
          />
        </TabsContent>

        <TabsContent value="inactive" className="space-y-4">
          <ApiKeyList
            keys={filteredKeys.filter(key => !key.is_active)}
            onToggleStatus={toggleKeyStatus}
            onRevoke={revokeKey}
            loading={loading}
          />
        </TabsContent>
      </Tabs>

      {/* Create API Key Modal */}
      <CreateApiKeyModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateKey}
        loading={loading}
      />

      {/* Success Modal (shows the full key once) */}
      {newKeyDetails && (
        <ApiKeySuccessModal
          open={successModalOpen}
          apiKey={newKeyDetails.key}
          keyName={newKeyDetails.name}
          onClose={handleCloseSuccessModal}
        />
      )}
    </div>
  );
};